// src/constants/drama.ts
var DRAMA_THRESHOLDS = {
  SAFE_MAX: 2,
  CAUTION_MIN: 2.1,
  CAUTION_MAX: 5,
  HIGH_DRAMA_MIN: 5.1,
  HIGH_DRAMA_MAX: 7,
  EXTREME_DRAMA_MIN: 7.1
};
var CATEGORY_COLORS = {
  safe: { bg: "#F0FDF4", text: "#166534", accent: "#22C55E" },
  caution: { bg: "#FFFBEB", text: "#92400E", accent: "#F59E0B" },
  high_drama: { bg: "#FEF2F2", text: "#991B1B", accent: "#EF4444" },
  extreme_drama: { bg: "#FEF2F2", text: "#7F1D1D", accent: "#DC2626" }
};
var CATEGORY_LABELS = {
  safe: "Safe",
  caution: "Caution",
  high_drama: "High Drama",
  extreme_drama: "Extreme Drama"
};
var SHARE_FORMAT_DIMENSIONS = {
  square: {
    width: 1080,
    height: 1080,
    safeHeight: 860,
    topPadding: 110,
    bottomPadding: 110,
    label: "Square (1:1)",
    platform: "Instagram Feed, Facebook"
  },
  portrait: {
    width: 1080,
    height: 1350,
    safeHeight: 1100,
    topPadding: 125,
    bottomPadding: 125,
    label: "Instagram Feed (4:5)",
    platform: "Instagram Feed (Primary)"
  },
  reel: {
    width: 1080,
    height: 1920,
    safeHeight: 1610,
    topPadding: 155,
    bottomPadding: 155,
    label: "Reel / Story (9:16)",
    platform: "Instagram Stories, TikTok, Shorts"
  },
  horizontal: {
    width: 1200,
    height: 630,
    safeHeight: 480,
    topPadding: 75,
    bottomPadding: 75,
    label: "Link Preview (16:9)",
    platform: "Twitter/X, LinkedIn, Web"
  }
};
function getDramaCategory(score) {
  if (score <= DRAMA_THRESHOLDS.SAFE_MAX) return "safe";
  if (score <= DRAMA_THRESHOLDS.CAUTION_MAX) return "caution";
  if (score <= DRAMA_THRESHOLDS.HIGH_DRAMA_MAX) return "high_drama";
  return "extreme_drama";
}
var ISSUE_DESCRIPTIONS = {
  "passive-aggressive": "Indirect expression of hostility or resentment",
  "blame-shifting": "Deflecting responsibility onto others",
  "emotional-manipulation": "Using emotions to control or influence",
  absolutes: 'Using absolute terms like "always" or "never"',
  dismissive: "Invalidating or ignoring feelings and concerns",
  "aggressive-tone": "Hostile, threatening, or confrontational language",
  defensiveness: "Excessive self-justification or counter-attacks",
  contempt: "Disrespect, mockery, or superiority"
};
var SEVERITY_WEIGHTS = {
  low: 1,
  medium: 2,
  high: 3.5,
  critical: 5
};
var DRAMA_KEYWORDS = {
  "passive-aggressive": [
    "fine",
    "whatever",
    "if you say so",
    "no worries",
    "suit yourself"
  ],
  "blame-shifting": [
    "it's your fault",
    "you made me",
    "because of you",
    "you always",
    "you never"
  ],
  "emotional-manipulation": [
    "you hurt me",
    "after all i did",
    "you owe me",
    "you make me feel"
  ],
  absolutes: [
    "always",
    "never",
    "everyone",
    "nobody",
    "every time",
    "not once"
  ],
  dismissive: [
    "you're overreacting",
    "it's not a big deal",
    "get over it",
    "stop being",
    "you're too sensitive"
  ],
  "aggressive-tone": ["shut up", "stupid", "idiot", "hate", "disgusting"],
  defensiveness: [
    "i didn't",
    "that's not what",
    "you misunderstood",
    "i was just",
    "you're wrong"
  ],
  contempt: ["pathetic", "ridiculous", "worthless", "loser", "whatever"]
};
var CATEGORY_RECOMMENDATIONS = {
  safe: [
    "Great communication! Keep it up.",
    "Your message is clear and respectful.",
    "This should facilitate positive dialogue."
  ],
  caution: [
    "Consider rephrasing for clarity.",
    "Watch out for potential misunderstandings.",
    "A few adjustments could improve reception."
  ],
  high_drama: [
    "Rewrite before sending.",
    "This message may escalate conflict.",
    "Consider a cooling-off period."
  ],
  extreme_drama: [
    "Do not send this message.",
    "Step away and revisit when calm.",
    "Seek mediation or professional guidance.",
    "Consider the long-term impact on the relationship."
  ]
};
function computeFallbackWeights(issues) {
  if (issues.length === 0) return [];
  const weights = issues.map((i) => SEVERITY_WEIGHTS[i.severity]);
  const total = weights.reduce((a, b) => a + b, 0);
  const rounded = total > 0 ? weights.map((w) => Math.round(w / total * 100) / 100) : weights.map(() => Math.round(1 / issues.length * 100) / 100);
  const sum = rounded.reduce((a, b) => a + b, 0);
  const diff = Math.round((1 - sum) * 100) / 100;
  if (diff !== 0 && rounded.length > 0) {
    rounded[rounded.length - 1] = Math.round((rounded[rounded.length - 1] + diff) * 100) / 100;
  }
  return rounded;
}
var CONTEXT_MULTIPLIERS = {
  work: 1.2,
  // Professional context requires extra care
  dating: 1.1,
  // Dating relationships are sensitive
  family: 1,
  // Baseline
  friends: 0.9,
  // Friends may be more forgiving
  social: 0.8,
  // Social contexts have lower stakes
  other: 1
  // Default
};

export {
  DRAMA_THRESHOLDS,
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  SHARE_FORMAT_DIMENSIONS,
  getDramaCategory,
  ISSUE_DESCRIPTIONS,
  SEVERITY_WEIGHTS,
  DRAMA_KEYWORDS,
  CATEGORY_RECOMMENDATIONS,
  computeFallbackWeights,
  CONTEXT_MULTIPLIERS
};
